import React, { useEffect, useState, ChangeEvent } from 'react';
import Layout from '../../components/Layout/Layout.component';
import { ButtonOne } from '../../components/ButtonOne/ButtonOne.component';
import { useStyles } from '../styles';
import { useStyles as myStyles } from './SupportInstructionDetails.styles';
import Breadcrumbs from '../../components/Breadcrumbs.component';
import { AxiosResponse } from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import { Grid, TextField, FormHelperTextProps } from '@material-ui/core';
import { ValidationRow } from '../../components/ValidationRow/ValidationRow.components';
import { LeavePrompt } from '../../components/Prompt/LeavePrompt/LeavePrompt.component';
import usePermission, { UserPermission } from '../../hooks/usePermission';
import { Paths } from '../../utils/routerUrls';
import {
  getInstruction,
  updateInstruction,
  createInstruction,
} from '../../services/support/instructions/supportInstructions.service';
import { IInstructionListItem } from '../../services/support/instructions/supportInstructions.types';
import { useLayoutContext } from '../../Context/Layout.context';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTranslation } from 'react-i18next';
import useDocumentTitle from '../../hooks/DocumentTitle/documentTitle';
import { sanitizeHtmlContent, modulesWithImages } from '../../utils/reactQuillUtils';
import { shortenText } from '../../utils/textHelper';
import useBase64Images, { getFilledHtmlContentImagePlaceholders } from '../../components/Support/useBase64Images';

const TITLE_MAX_LENGTH = 256;
const DESCRIPTION_MAX_LENGTH = 256;

const SupportInstructionDetails: React.FC = () => {
  const classes = useStyles();
  const myClasses = myStyles();
  const history = useHistory();
  const { setIsLoader, showLoader } = useLayoutContext();
  const isAdminPermission = usePermission(UserPermission.AdminSupportManage);

  const { instructionId, action } = useParams() as {
    instructionId: string;
    action: string;
  };

  const [editing, setEditing] = useState<boolean>(isAdminPermission && action == 'edit');
  const [adding, setAdding] = useState<boolean>(isAdminPermission && action == 'add');
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [isHtmlEditor, setIsHtmlEditor] = useState<boolean>(true);
  const [redirectToList, setRedirectToList] = useState<boolean>(false);
  const [instruction, setInstruction] = useState<IInstructionListItem>();
  const [title, setTitle] = useState<string>(instruction?.title ?? '');
  const [description, setDescription] = useState<string>(instruction?.description ?? '');
  const [htmlContent, setHtmlContent] = useState<string>(instruction?.htmlContent ?? '');
  const [rawHtmlContent, setRawHtmlContent] = useState<string>(instruction?.htmlContent ?? '');
  const [decodedHtmlContent, setDecodedHtmlContent] = useState<string>();
  const { t } = useTranslation();
  const { htmlContentWithoutImages, images } = useBase64Images(htmlContent);
  useDocumentTitle("Instrukcje interaktywne");

  const { openAlert } = useLayoutContext();

  const refreshData = () => {
    if (adding) {
      setInstruction({ id: '0', title: '', description: '', htmlContent: '', isLiked: false, images: [] });
      return;
    }

    setIsLoader(true);

    getInstruction(instructionId)
      .then((response: AxiosResponse<IInstructionListItem>) => {
        setInstruction(response.data);

        const { title, description, htmlContent, images } = response.data;
        setTitle(title);
        setDescription(description);

        const fullHtmlContent = getFilledHtmlContentImagePlaceholders(htmlContent, images);
        setHtmlContent(decodeURIComponent(window.atob(fullHtmlContent)));
        setDecodedHtmlContent(decodeURIComponent(window.atob(fullHtmlContent)));
      })
      .catch(() => {
        console.error('error');
      })
      .finally(() => setIsLoader(false));
  };

  useEffect(() => {
    refreshData();
  }, [instructionId]);

  const [hasTitleFocus, setHasTitleFocus] = useState<boolean>(false);
  const isTitleTooLong = title.length > TITLE_MAX_LENGTH;
  const handleTitleChange = (value: string) => {
    setTitle(value);
  };

  const [hasDescriptionFocus, setHasDescriptionFocus] = useState<boolean>(false);
  const isDescriptionTooLong = description.length > DESCRIPTION_MAX_LENGTH;
  const handleDescriptionChange = (value: string) => {
    setDescription(value);
  };

  const showDetails = (instructionId: string) => {
    setEditing(false);
    setAdding(false);
    setIsLeavePromptOpen(false);
    history.push(`${Paths.supportInstructions}/${instructionId}`);
    refreshData();
  };

  const saveInstruction = async () => {
    let detailsInstructionId = instructionId;
    showLoader(true, 'Zapisywanie');
    if (adding) {
      createInstruction(title, description, htmlContentWithoutImages, images).then(
        (response) => {
          openAlert('success');
          detailsInstructionId = response.data;
          showLoader(false);
          showDetails(detailsInstructionId);
        },
        () => {
          openAlert('save_error');
          showLoader(false);
        },
      );
    } else if (editing) {
      updateInstruction(instructionId, title, description, htmlContentWithoutImages, images).then(
        () => {
          openAlert('success');
          showLoader(false);
          showDetails(detailsInstructionId);
        },
        () => {
          openAlert('save_error');
          showLoader(false);
        },
      );
    }
  };

  const handleSetEditing = () => {
    history.push(`${Paths.supportInstructions}/${instructionId}/edit`);
    setEditing(true);
  };

  const redirectToListAction = () => {
    history.push(Paths.supportInstructions);
  };

  const handleButtonListClick = () => {
    if (editing || adding) {
      setRedirectToList(true);
      setIsLeavePromptOpen(true);
      return;
    }

    redirectToListAction();
  };

  const redirectToListBreadcrumbs = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    handleButtonListClick();
  };

  const handleLeavePrompConfirm = () => {
    redirectToList ? redirectToListAction() : showDetails(instructionId);
  };

  const handleSaveRawHtmlContent = (htmlContent: string) => {
    setRawHtmlContent(htmlContent);
    setHtmlContent(htmlContent);
  };

  const switchHtmlEditor = () => {
    isHtmlEditor ? setRawHtmlContent(htmlContent) : setHtmlContent(rawHtmlContent);
    setIsHtmlEditor(!isHtmlEditor);
  };

  useEffect(() => {
    if (!instruction || !title.length || !description.length) return;
    if (decodedHtmlContent && decodedHtmlContent.length) {
      setIsDataChanged(
        title !== instruction?.title || description !== instruction?.description || htmlContent !== decodedHtmlContent,
      );
    } else if (htmlContent.length && !decodedHtmlContent) {
      setIsDataChanged(true);
    } else {
      setIsDataChanged(title !== instruction?.title || description !== instruction?.description);
    }
  }, [title, description, htmlContent, instruction, decodedHtmlContent]);

  return (
    <>
      <Layout viewType={isAdminPermission ? 'admin' : 'fullwidthForExaminer'}>
        <div
          className={classes.tableAndBreadcrumContainer}
          style={{
            width: '100%',
            overflow: 'scroll',
            padding: '20px',
          }}
        >
          <div
            className={classes.breadcrumbHeader}
            style={{
              maxWidth:
                (!isHtmlEditor && rawHtmlContent) || (!editing && !adding && (rawHtmlContent || htmlContent))
                  ? 'calc(100% - 220px)'
                  : '100%',
            }}
          >
            <Breadcrumbs
              onCurrentListClick={redirectToListBreadcrumbs}
              listRoute={Paths.supportInstructions}
              currentList="Instrukcje interaktywne"
              currentElement={
                instruction?.title === undefined || instruction?.title === ''
                  ? 'Instrukcja'
                  : shortenText(instruction?.title, 50)
              }
              mainElementRoute={Paths.support}
              mainElement={`Centrum pomocy ${t('global:shortNames.beneficiary')} ${t('global:shortNames.system')}`}
            />

            <div className={classes.buttonsContainer}>
              <ButtonOne
                className={classes.primary}
                style={{ marginRight: 20 }}
                variant="contained"
                aria-label="Lista instrukcji"
                onClick={handleButtonListClick}
              >
                Lista instrukcji
              </ButtonOne>

              {editing && (
                <ButtonOne
                  className={classes.primary}
                  style={{ marginRight: 20 }}
                  variant="contained"
                  aria-label="Anuluj"
                  onClick={() => (isDataChanged ? setIsLeavePromptOpen(true) : handleLeavePrompConfirm())}
                >
                  Anuluj
                </ButtonOne>
              )}

              {isAdminPermission && !editing && !adding && (
                <ButtonOne
                  className={classes.primary}
                  style={{ marginRight: 20 }}
                  variant="contained"
                  aria-label="Edytuj"
                  onClick={handleSetEditing}
                >
                  Edytuj
                </ButtonOne>
              )}

              {(editing || adding) && (
                <ButtonOne
                  className={classes.primary}
                  variant="contained"
                  aria-label="Zapisz"
                  disabled={
                    !isDataChanged || isTitleTooLong || isDescriptionTooLong || title === '' || description === ''
                  }
                  onClick={saveInstruction}
                >
                  Zapisz
                </ButtonOne>
              )}
            </div>
          </div>
          {instruction && !editing && !adding && (
            <div
              className={myClasses.item}
              style={{ maxWidth: rawHtmlContent || htmlContent ? 'calc(100% - 290px)' : '100%' }}
            >
              <h1>{instruction.title}</h1>
              <h3>{instruction.description}</h3>
              {<div className="ql-editor" dangerouslySetInnerHTML={{ __html: sanitizeHtmlContent(htmlContent) }}></div>}
            </div>
          )}
          {instruction && (editing || adding) && (
            <>
              <Grid
                container
                className={myClasses.item}
                style={{ maxWidth: !isHtmlEditor && rawHtmlContent ? 'calc(100% - 230px)' : '100%' }}
              >
                <Grid item xs={12}>
                  <TextField
                    value={title}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleTitleChange(e.target.value)}
                    label="Tytuł*"
                    variant="outlined"
                    fullWidth
                    onFocus={() => setHasTitleFocus(true)}
                    onBlur={() => setHasTitleFocus(false)}
                    error={isTitleTooLong}
                    FormHelperTextProps={
                      {
                        component: 'div',
                      } as Partial<FormHelperTextProps<'p'>>
                    }
                    helperText={
                      <ValidationRow
                        helperText={isTitleTooLong ? 'Podany tytuł jest za długi' : ''}
                        limitsVisible={hasTitleFocus || isTitleTooLong}
                        valueLength={title.length}
                        limit={TITLE_MAX_LENGTH}
                        withError={isTitleTooLong}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12} className={myClasses.helperRow}></Grid>
                <Grid item xs={12}>
                  <TextField
                    value={description}
                    multiline
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleDescriptionChange(e.target.value)}
                    label="Krótki opis*"
                    variant="outlined"
                    fullWidth
                    inputProps={{
                      style: {
                        height: '60px',
                      },
                    }}
                    onFocus={() => setHasDescriptionFocus(true)}
                    onBlur={() => setHasDescriptionFocus(false)}
                    error={isDescriptionTooLong}
                    FormHelperTextProps={
                      {
                        component: 'div',
                      } as Partial<FormHelperTextProps<'p'>>
                    }
                    helperText={
                      <ValidationRow
                        helperText={isDescriptionTooLong ? 'Podana odpowiedź jest za długa' : ''}
                        limitsVisible={hasDescriptionFocus || isDescriptionTooLong}
                        valueLength={description.length}
                        limit={DESCRIPTION_MAX_LENGTH}
                        withError={isDescriptionTooLong}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <div style={{ textAlign: 'left' }}>
                    <ButtonOne
                      className={classes.primary}
                      style={{ margin: 10 }}
                      variant="contained"
                      aria-label="Edytuj"
                      onClick={switchHtmlEditor}
                    >
                      {isHtmlEditor ? 'Kod HTML' : 'Edytor HTML'}
                    </ButtonOne>
                  </div>
                  {isHtmlEditor && (
                    <ReactQuill
                      theme="snow"
                      value={sanitizeHtmlContent(htmlContent)}
                      className={classes.reactQuill}
                      onChange={(value) => {
                        setHtmlContent(sanitizeHtmlContent(value));
                        setIsDataChanged(true);
                      }}
                      modules={modulesWithImages}
                    />
                  )}

                  {!isHtmlEditor && (
                    <TextField
                      value={rawHtmlContent}
                      multiline
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleSaveRawHtmlContent(e.target.value)}
                      label="Treść HTML*"
                      variant="outlined"
                      fullWidth
                      minRows="7"
                      maxRows="15"
                      FormHelperTextProps={
                        {
                          component: 'div',
                        } as Partial<FormHelperTextProps<'p'>>
                      }
                    />
                  )}
                </Grid>
              </Grid>
              {!isHtmlEditor && (
                <div className={myClasses.item} style={{ maxWidth: rawHtmlContent ? 'calc(100% - 290px)' : '100%' }}>
                  <h3>Podgląd HTML:</h3>
                  {
                    <div
                      className="ql-editor"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtmlContent(rawHtmlContent) }}
                    />
                  }
                </div>
              )}
            </>
          )}
          <LeavePrompt
            isOpen={isLeavePromptOpen}
            onConfirm={handleLeavePrompConfirm}
            onCancel={() => setIsLeavePromptOpen(false)}
          />
        </div>
      </Layout>
    </>
  );
};

export default SupportInstructionDetails;
